import { useEffect } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import { Prompt } from 'react-router';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import {
  isValidRecurringInterval,
  RecurringIntervalInput,
} from 'dashboard/forms/FormReportTemplate/RecurringIntervalInput';
import RecurringReportFormWrapper from 'dashboard/forms/FormReportTemplate/RecurringReportFormWrapper';
import {
  ReportTemplateFormRow,
  ReportTemplateFormRowField,
  ReportTemplateFormRowLabelWrapper,
} from 'dashboard/forms/FormReportTemplate/ReportTemplateFieldRow';
import ReportTemplateHeader from 'dashboard/forms/FormReportTemplate/ReportTemplateHeader';
import Section from 'dashboard/layouts/FormLayout/Section';
import { ColumnContainer, SoloColumn } from 'dashboard/layouts/ShowViewLayoutV2';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { FontWeight } from 'design-system/variables/text';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormReportTemplateCryptoAddressWatchlistProps {
  canEdit: boolean;
  submit: (data: ReportTemplateCryptoAddressWatchlistConfigFormData) => void;
  defaultValues: ReportTemplateCryptoAddressWatchlistConfigFormData;
  isLoadingPage: boolean;
  latestPublishedVersionId: string;
  helpCenterUrl?: string;
}

export const FormReportTemplateCryptoAddressWatchlist = ({
  canEdit,
  submit,
  defaultValues,
  isLoadingPage,
  latestPublishedVersionId,
  helpCenterUrl,
}: FormReportTemplateCryptoAddressWatchlistProps) => {
  const { register, handleSubmit, control, watch, getValues, reset } =
    useFormSecurely<ReportTemplateCryptoAddressWatchlistConfigFormData>({
      defaultValues,
    });

  const { isSubmitting, isDirty, errors, isValid, isSubmitted } = useFormState({
    control,
  });
  const disabled = !canEdit || isLoadingPage || isSubmitting;
  const intervalSeconds = watch('interval-seconds');

  useEffect(() => {
    if (isSubmitted) {
      reset(getValues());
    }
  }, [isSubmitted, getValues, reset]);

  return (
    <>
      <Prompt
        when={isDirty && !isSubmitting}
        message='You have unsaved changes. Are you sure you want to leave?'
      />
      <form onSubmit={handleSubmit(submit)}>
        <ReportTemplateHeader
          saveDisabled={disabled || !isDirty || !isValid}
          reportTemplateVersionId={latestPublishedVersionId}
        />
        <ColumnContainer>
          <SoloColumn>
            <RecurringReportFormWrapper
              continuousEnabled={intervalSeconds != null}
              canEdit={canEdit}
              configContent={
                <>
                  <Card>
                    <Card.Body>
                      <Section cols={1}>
                        <StyledLabel
                          size='lg'
                          htmlFor='form-report-template-CryptoAddressWatchlist__name'
                        >
                          Template name
                        </StyledLabel>
                        <Spacer height={2} />
                        <Input
                          {...register('name', {
                            required: 'A template name is required',
                          })}
                          id='form-report-template-CryptoAddressWatchlist__name'
                          type='text'
                          placeholder='Name'
                          disabled={disabled}
                        />
                        {errors.name && <FormError>{errors.name.message}</FormError>}
                      </Section>
                    </Card.Body>
                  </Card>

                  {helpCenterUrl && (
                    <Card>
                      <Card.Body>
                        <StyledLabel size='lg'>Additional resources</StyledLabel>
                        <span>
                          Learn more about configuring this report on the{' '}
                          <a href={helpCenterUrl} target='_blank' rel='noreferrer'>
                            help center
                          </a>
                          .
                        </span>
                      </Card.Body>
                    </Card>
                  )}
                </>
              }
              recurringContent={
                <Card>
                  <Card.Body>
                    <StyledLabel size='lg'>Continuous monitoring</StyledLabel>
                    <ReportTemplateFormRow noBorder={true}>
                      <ReportTemplateFormRowLabelWrapper>
                        <Label size='lg' weight='bold'>
                          Recurring interval
                        </Label>
                        <Paragraph>Set a recurring cadence to run this report</Paragraph>
                      </ReportTemplateFormRowLabelWrapper>
                      <ReportTemplateFormRowField>
                        <Controller
                          control={control}
                          name='interval-seconds'
                          rules={{
                            validate: (value) =>
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              isValidRecurringInterval(value) ||
                              'Please enter an interval between 1 and 365 days',
                          }}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <RecurringIntervalInput
                              id='form-report-template-crypto-address-watchlist__interval-seconds'
                              name='interval-seconds'
                              disabled={true}
                              onChange={onChange}
                              onBlur={onBlur}
                              value={value}
                            />
                          )}
                        />
                      </ReportTemplateFormRowField>
                    </ReportTemplateFormRow>
                    <ReportTemplateFormRow noBorder={true} initialColumnWidth='70%'>
                      <ReportTemplateFormRowLabelWrapper>
                        <Label size='lg' weight='bold'>
                          Pause continuous monitoring upon match
                        </Label>
                        <Paragraph>
                          When enabled: if there is a match, recurrence will pause until all matches
                          are dismissed.
                        </Paragraph>
                        <Paragraph>
                          When disabled: reports will rerun regardless of match. This will likely
                          result in the same matches being surfaced again, unless they have been
                          dismissed.
                        </Paragraph>
                      </ReportTemplateFormRowLabelWrapper>
                      <ReportTemplateFormRowField>
                        <Controller
                          control={control}
                          name='always-recur'
                          render={({ field: { onChange, value } }) => (
                            <ToggleSlider
                              onChange={onChange}
                              value={!value}
                              labelOn='Enabled'
                              labelOff='Disabled'
                              disabled={true}
                            />
                          )}
                        />
                      </ReportTemplateFormRowField>
                    </ReportTemplateFormRow>
                  </Card.Body>
                </Card>
              }
            />
          </SoloColumn>
        </ColumnContainer>
      </form>
    </>
  );
};

export interface ReportTemplateCryptoAddressWatchlistConfigFormData {
  name: string;
  'interval-seconds'?: number;
  'always-recur'?: boolean;
}

const StyledLabel = styled(Label)`
  font-weight: ${FontWeight.Bold};
`;
